/**
 * PATH: apps/quote/src/pages/claims/NewClaimPage.tsx
 * FILE_ID: QUOTE-CLAIMS-PAGE-NEW-001
 * PURPOSE: Explicit claim setup before any server-side generation.
 * WHY: Route entry must never create a financial document without confirmation.
 * LANDMINE: QuoteListItem.job_number currently carries the integer job pk.
 */
import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, AlertCircle, FileText, Loader2, Receipt, Search } from 'lucide-react'
import { formatCurrency } from '@/lib/utils'
import { useApi } from '@/lib/setupApi'
import { quotesCrudApi } from '../../lib/api/quotes-crud-api'
import type { QuoteListItem } from '../../lib/quote-api-types'
import { ExternalPriorPage } from './ExternalPriorPage'
import {
  ensureClaimContract,
  generateConfiguredClaim,
  listClaimContracts,
  listClaimProfiles,
  type ClaimProfile,
  type ClaimSetupRequest,
} from '@quotech/api/g10ProgressClaims'

type DocType = 'claim' | 'invoice'

function useGenerateClaim() {
  const api = useApi()
  const navigate = useNavigate()
  return useMutation({
    mutationFn: async (request: Omit<ClaimSetupRequest, 'contract_id'> & {
      quoteId: string | null
      contractId: string | null
      profileId: string | null
      paymentTermsDays: number
    }) => {
      const contract = await ensureClaimContract(api, {
        jobId: request.job_id,
        contractId: request.contractId,
        quoteId: request.quoteId,
        profileId: request.profileId,
        paymentTermsDays: request.paymentTermsDays,
        retentionPercent: request.retention_percent,
      })
      return generateConfiguredClaim(api, {
        job_id: request.job_id,
        contract_id: contract.id,
        doc_type: request.doc_type,
        period: request.period,
        period_start: request.period_start,
        period_end: request.period_end,
        retention_percent: request.retention_percent,
      })
    },
    onSuccess: (claim) => navigate(`/claims/${claim.id}`, { replace: true }),
  })
}

function useClaimableQuotes(enabled: boolean) {
  return useQuery({
    queryKey: ['claimable-quotes'],
    queryFn: () => quotesCrudApi.listQuotes({ status: 'accepted' }),
    enabled,
    select: (quotes: QuoteListItem[]) =>
      quotes.filter(
        (q) => (q.quote_type ?? 'quote') === 'quote' && q.job_number,
      ),
  })
}

function useQuoteContextJob(quoteId: string | null) {
  return useQuery({
    queryKey: ['claim-source-quote', quoteId],
    queryFn: async () => {
      const quote = await quotesCrudApi.getQuote(quoteId as string)
      return (quote as { job_id?: string | null }).job_id ?? null
    },
    enabled: !!quoteId,
  })
}

const today = new Date().toISOString().slice(0, 10)
const monthStart = `${today.slice(0, 8)}01`
const FIELD = 'mt-1 min-h-11 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-900'

export default function NewClaimPage() {
  const [params] = useSearchParams()
  const quoteId = params.get('quoteId')
  const jobIdParam = params.get('jobId')
  const docType: DocType = params.get('docType') === 'invoice' ? 'invoice' : 'claim'
  const docLabel = docType === 'invoice' ? 'tax invoice' : 'claim'
  const api = useApi()
  const generate = useGenerateClaim()
  const contextJob = useQuoteContextJob(quoteId)
  const quotes = useClaimableQuotes(!quoteId && !jobIdParam)
  const contracts = useQuery({
    queryKey: ['claim-contracts', jobIdParam],
    queryFn: () => listClaimContracts(api, Number(jobIdParam)),
    enabled: Boolean(jobIdParam),
  })
  const profiles = useQuery<ClaimProfile[]>({
    queryKey: ['claim-profiles', 'active'],
    queryFn: () => listClaimProfiles(api),
    retry: false,
  })
  const [search, setSearch] = useState('')
  const [jobId, setJobId] = useState(jobIdParam ?? '')
  const [selectedQuoteId, setSelectedQuoteId] = useState(quoteId ?? '')
  const [selectedContractId, setSelectedContractId] = useState('')
  const [periodStart, setPeriodStart] = useState(monthStart)
  const [periodEnd, setPeriodEnd] = useState(today)
  const [profileId, setProfileId] = useState('')
  const [retention, setRetention] = useState('0')
  const [paymentDays, setPaymentDays] = useState('14')
  const [attempted, setAttempted] = useState(false)

  useEffect(() => {
    if (contextJob.data) setJobId(String(contextJob.data))
  }, [contextJob.data])
  useEffect(() => {
    const preferred = profiles.data?.find((profile) => profile.is_default && profile.is_active)
    if (preferred) setProfileId(preferred.id)
  }, [profiles.data])
  useEffect(() => {
    if (!selectedContractId && contracts.data?.[0]) {
      setSelectedContractId(contracts.data[0].id)
    }
  }, [contracts.data, selectedContractId])

  const filtered = useMemo(() => {
    const rows = quotes.data ?? []
    if (!search.trim()) return rows
    const q = search.toLowerCase()
    return rows.filter(
      (row) =>
        row.quote_number.toLowerCase().includes(q) ||
        (row.project_name ?? '').toLowerCase().includes(q) ||
        (row.client_name ?? '').toLowerCase().includes(q) ||
        (row.job_number ?? '').includes(q),
    )
  }, [quotes.data, search])

  const errorDetail =
    (generate.error as { response?: { data?: { detail?: string } } } | null)
      ?.response?.data?.detail ?? null

  const errors = [
    !jobId ? 'Choose a contract or work package.' : '',
    jobIdParam && !selectedContractId ? 'Choose a claim contract for this job.' : '',
    !periodStart || !periodEnd ? 'Enter the claim period.' : '',
    periodStart > periodEnd ? 'Period start must be before period end.' : '',
    Number(retention) < 0 || Number(retention) > 100 ? 'Retention must be between 0 and 100%.' : '',
    Number(paymentDays) < 0 ? 'Payment terms cannot be negative.' : '',
  ].filter(Boolean)
  if (params.get('mode') === 'upload' && jobIdParam) {
    return <ExternalPriorPage jobId={Number(jobIdParam)} />
  }
  const submit = () => {
    setAttempted(true)
    if (errors.length) return
    generate.mutate({
      job_id: Number(jobId),
      doc_type: docType,
      period: `${periodStart} to ${periodEnd}`,
      period_start: periodStart,
      period_end: periodEnd,
      retention_percent: Number(retention),
      quoteId: selectedQuoteId || null,
      contractId: selectedContractId || null,
      profileId: profileId || null,
      paymentTermsDays: Number(paymentDays),
    })
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 flex items-center gap-4">
          <Link to="/claims" className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              {docType === 'invoice' ? 'New Tax Invoice' : 'New Payment Claim'}
            </h1>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
              Confirm the contract, period and document controls before creating anything.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        {(errorDetail || (attempted && errors.length > 0)) && (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
            <p className="font-medium">{errorDetail || 'Review the setup details.'}</p>
            {attempted && errors.map((error) => <p key={error}>• {error}</p>)}
          </div>
        )}

        <section className="rounded-xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-800">
          <h2 className="font-semibold text-slate-900 dark:text-white">1. Contract or work package</h2>
          {jobIdParam ? (
            <label className="mt-3 block text-sm font-medium">Claim contract<select value={selectedContractId} onChange={(event) => setSelectedContractId(event.target.value)} className={FIELD}><option value="">Select contract</option>{contracts.data?.map((contract) => <option key={contract.id} value={contract.id}>{contract.title}{contract.work_package ? ` · ${contract.work_package}` : ''}</option>)}</select>{contracts.isError && <span className="mt-1 block text-xs text-red-600">Contracts could not be loaded.</span>}</label>
          ) : quoteId ? (
            <div className="mt-3 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:bg-amber-900/20 dark:text-amber-200">
              {contextJob.isLoading ? 'Resolving quote contract…' : jobId ? `Selected job ${jobId}` : 'This quote is not linked to a job.'}
            </div>
          ) : (
            <>
              <label className="relative mt-3 block max-w-md">
                <span className="sr-only">Search contracts</span>
                <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search contracts, jobs, or clients…" className="w-full rounded-lg border border-slate-200 py-2.5 pl-10 pr-4 text-sm dark:border-slate-700 dark:bg-slate-900" />
              </label>
              <div className="mt-3 max-h-64 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700">
                {quotes.isLoading ? <CenteredSpinner label="Loading contracts…" /> : filtered.length === 0 ? (
                  <p className="p-6 text-center text-sm text-slate-500">No accepted linked contracts found.</p>
                ) : filtered.map((quote) => (
                  <button key={quote.id} type="button" onClick={() => { setJobId(String(quote.job_number)); setSelectedQuoteId(quote.id) }} aria-pressed={selectedQuoteId === quote.id} className={`flex w-full items-center justify-between gap-3 border-b px-4 py-3 text-left last:border-0 ${selectedQuoteId === quote.id ? 'bg-amber-50 ring-1 ring-inset ring-amber-400 dark:bg-amber-900/20' : 'hover:bg-slate-50 dark:hover:bg-slate-700'}`}>
                    <span><span className="block font-medium">{quote.quote_number} · {quote.project_name}</span><span className="text-xs text-slate-500">Job {quote.job_number} · {quote.client_name}</span></span>
                    <span className="flex items-center gap-2 font-mono text-sm"><span>{formatCurrency(Number(quote.total_excl_gst ?? 0))}</span><Receipt className="h-4 w-4 text-amber-500" /></span>
                  </button>
                ))}
              </div>
            </>
          )}
        </section>

        <section className="grid gap-4 rounded-xl border border-slate-200 bg-white p-5 sm:grid-cols-2 dark:border-slate-700 dark:bg-slate-800">
          <h2 className="sm:col-span-2 font-semibold">2. Period and document profile</h2>
          <Field label="Period start"><input type="date" value={periodStart} onChange={(event) => setPeriodStart(event.target.value)} className={FIELD} /></Field>
          <Field label="Period end"><input type="date" value={periodEnd} onChange={(event) => setPeriodEnd(event.target.value)} className={FIELD} /></Field>
          <Field label="Profile"><select value={profileId} onChange={(event) => setProfileId(event.target.value)} className={FIELD}><option value="">Organisation default</option>{profiles.data?.filter((profile) => profile.is_active).map((profile) => <option key={profile.id} value={profile.id}>{profile.name} · v{profile.mapping_version}</option>)}</select></Field>
          {profiles.isError && <p className="sm:col-span-2 flex gap-2 text-xs text-amber-700"><AlertCircle className="h-4 w-4" />Profile service is unavailable. The organisation default will be requested.</p>}
        </section>

        <section className="grid gap-4 rounded-xl border border-slate-200 bg-white p-5 sm:grid-cols-2 dark:border-slate-700 dark:bg-slate-800">
          <h2 className="sm:col-span-2 font-semibold">3. Retention and payment summary</h2>
          <Field label="Retention %"><input type="number" min="0" max="100" step="0.1" value={retention} onChange={(event) => setRetention(event.target.value)} className={FIELD} /></Field>
          <Field label="Payment terms (days)"><input type="number" min="0" value={paymentDays} onChange={(event) => setPaymentDays(event.target.value)} className={FIELD} /></Field>
          <div className="sm:col-span-2 rounded-lg bg-slate-50 p-3 text-sm text-slate-600 dark:bg-slate-900 dark:text-slate-300">Retention will be shown using the selected profile. Payment is due {paymentDays || 0} days after issue. Values remain editable until submission.</div>
        </section>

        <div className="flex justify-end">
          <button type="button" onClick={submit} disabled={generate.isPending || contextJob.isLoading} className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-amber-500 px-5 py-2.5 font-medium text-white hover:bg-amber-600 disabled:opacity-50">
            {generate.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : <FileText className="h-5 w-5" />}
            {generate.isPending ? `Creating ${docLabel}…` : `Create draft ${docLabel}`}
          </button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="text-sm font-medium text-slate-700 dark:text-slate-300">{label}{children}</label>
}

function CenteredSpinner({ label }: { label?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-slate-500">
      <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      {label && <p className="text-sm mt-3">{label}</p>}
    </div>
  )
}
